import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TableInstance } from 'react-table';
import { useFilters, useSortBy, useTable } from 'react-table';

import type { Dispatch } from '@swf/core/store';
import type { Tournament } from '@swf/poker';
import { MobileTable } from '@swf/poker';

import useTableRowsCount from '../../../hooks/useTableRowsCount';
import useTableSearchFilter from '../../../hooks/useTableSearchFilter';
import { usePokerMultiTableTournamentsWidget } from '../../../model';
import { INITIAL_ACTIVE_UI_FILTERS } from '../../../model/defaultState';
import { ModalRoutes } from '../../../types';

import styles from './Table.scss';
import { COLUMNS } from './columns';

const Table: React.FC = () => {
  const { widgetSelect, widgetDispatch, widgetId } = usePokerMultiTableTournamentsWidget();
  const { modal } = useDispatch<Dispatch>();
  const loading = useSelector(widgetSelect.isLoading);
  const filters = useSelector(widgetSelect.getTableFilters);
  const sortRule = useSelector(widgetSelect.getTableSortRule);
  const selectedRowId = useSelector(widgetSelect.getSelectedTournamentId);
  const tournamentList = useSelector(widgetSelect.getTournamentList);

  const tableProps: TableInstance<Tournament> = useTable<Tournament>(
    {
      columns: COLUMNS,
      data: tournamentList,
      initialState: { sortBy: [sortRule], filters }
    },
    useFilters,
    useSortBy
  );

  useTableRowsCount(tableProps.rows.length);
  useTableSearchFilter(filters, tableProps.setFilter);

  useEffect(() => {
    widgetDispatch.setActiveUiFilters(INITIAL_ACTIVE_UI_FILTERS);
    widgetDispatch.resetActiveFilters({ upcomingOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (tournamentId: number) =>
    modal.enqueue({
      path: ModalRoutes.MobileSidecarScreen,
      locationState: { widgetId, tournamentId }
    });

  return (
    <MobileTable
      className={styles.table}
      tableProps={tableProps}
      loading={loading}
      selectedRowId={selectedRowId}
      handleRowClick={handleRowClick}
    />
  );
};

export default Table;
